import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Heart, MessageCircle, Eye,
  User, Send, Trash2, Edit
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';

const ForumPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    fetchPost();
    fetchCurrentUser();
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/forum/post/${postId}`);
      setPost(response.data);
    } catch (err) {
      console.error('Failed to fetch post:', err);
      toast.error(t('forum.postPage.loadFailed'));
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUserId(response.data.user_id);
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  };

  const handleLikePost = async () => {
    try {
      const response = await api.post(`/forum/post/${postId}/like`);
      setPost(prev => ({
        ...prev,
        like_count: response.data.like_count
      }));
    } catch (err) {
      toast.error(t('forum.postPage.likeFailed'));
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm(t('forum.postPage.deletePostConfirm'))) return;
    
    try {
      await api.delete(`/forum/post/${postId}`);
      toast.success(t('forum.postPage.postDeleted'));
      navigate('/community');
    } catch (err) {
      toast.error(t('forum.postPage.postDeleteFailed'));
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      setSubmitting(true);
      const response = await api.post(`/forum/post/${postId}/comment`, {
        content: newComment
      });
      setPost(prev => ({
        ...prev,
        comments: [...(prev.comments || []), response.data]
      }));
      setNewComment('');
      toast.success(t('forum.postPage.commentAdded'));
    } catch (err) {
      toast.error(t('forum.postPage.commentAddFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(t('forum.postPage.deleteCommentConfirm'))) return;
    
    try {
      await api.delete(`/forum/comment/${commentId}`);
      setPost(prev => ({
        ...prev,
        comments: prev.comments.filter(c => c.comment_id !== commentId)
      }));
      toast.success(t('forum.postPage.commentDeleted'));
    } catch (err) {
      toast.error(t('forum.postPage.commentDeleteFailed'));
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      await api.post(`/forum/comment/${commentId}/like`);
      fetchPost();
    } catch (err) {
      toast.error(t('forum.postPage.likeCommentFailed'));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('forum.postPage.back')}
        </button>

        {/* Post */}
        <article className="rounded-xl bg-slate-800 border border-slate-700 overflow-hidden mb-6">
          {/* Post Header */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-slate-700">
                  <User className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{post.author_name}</p>
                  <p className="text-sm text-slate-500">{formatDate(post.created_at)}</p>
                </div>
              </div>
              
              {currentUserId === post.author_id && (
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/community/forum/post/${postId}/edit`)}
                    className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-400 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleDeletePost}
                    className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            
            {post.language_code && (
              <span className="inline-block px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-sm mb-4">
                {post.language_name || post.language_code}
              </span>
            )}
            
            <h1 className="text-2xl font-bold text-white">{post.title}</h1>
          </div>

          {/* Post Content */}
          <div className="p-6">
            <p className="text-slate-300 whitespace-pre-wrap">{post.content}</p>
          </div>

          {/* Post Actions */}
          <div className="px-6 py-4 border-t border-slate-700 flex items-center gap-6">
            <button
              onClick={handleLikePost}
              className="flex items-center gap-2 text-slate-400 hover:text-red-400 transition-colors"
            >
              <Heart className="w-5 h-5" />
              <span>{post.like_count || 0}</span>
            </button>
            <span className="flex items-center gap-2 text-slate-500">
              <MessageCircle className="w-5 h-5" />
              {post.comments?.length || 0}
            </span>
            <span className="flex items-center gap-2 text-slate-500">
              <Eye className="w-5 h-5" />
              {post.view_count || 0}
            </span>
          </div>
        </article>

        {/* Comments Section */}
        <section>
          <h2 className="text-xl font-semibold text-white mb-4">
            {t('forum.postPage.comments', { count: post.comments?.length || 0 })}
          </h2>

          {/* Add Comment */}
          <form onSubmit={handleSubmitComment} className="mb-6">
            <div className="flex gap-3">
              <div className="p-2 rounded-full bg-slate-800">
                <User className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t('forum.postPage.commentPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
                  maxLength={1000}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-slate-500">{newComment.length}/1000</span>
                  <button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? t('forum.postPage.posting') : t('forum.postPage.postComment')}
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Comments List */}
          <div className="space-y-4">
            {post.comments?.map((comment) => (
              <div
                key={comment.comment_id}
                className="p-4 rounded-xl bg-slate-800 border border-slate-700"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-slate-700">
                      <User className="w-4 h-4 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{comment.author_name}</p>
                      <p className="text-xs text-slate-500">{formatDate(comment.created_at)}</p>
                    </div>
                  </div>
                  
                  {currentUserId === comment.author_id && (
                    <button
                      onClick={() => handleDeleteComment(comment.comment_id)}
                      className="p-1 rounded text-slate-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <p className="text-slate-300 text-sm mb-3">{comment.content}</p>
                
                <button
                  onClick={() => handleLikeComment(comment.comment_id)}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-400 transition-colors"
                >
                  <Heart className="w-4 h-4" />
                  <span>{comment.like_count || 0}</span>
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ForumPost;
